/**
 * What the screens read, and how they learn it changed.
 *
 * A single hook rather than a state library: everything on screen comes from IndexedDB, and
 * the only question is when to read it again. Actions reload after they write, and syncing
 * reloads when it finds something new.
 */

import { useCallback, useEffect, useState } from 'preact/hooks';

import type { Grocery, InventoryItem, Location, ShoppingEntry } from '../api/types';
import type { Database } from '../store/database';
import { groceries, items, locations, shoppingList } from '../store/entities';
import { pendingCount } from '../store/outbox';

export interface HouseholdView {
  items: InventoryItem[];
  locations: Location[];
  /** The household's own list of what it buys, which decides where an arrival goes. */
  groceries: Grocery[];
  shopping: ShoppingEntry[];
  queued: number;
  loaded: boolean;
}

const EMPTY: HouseholdView = {
  items: [],
  locations: [],
  groceries: [],
  shopping: [],
  queued: 0,
  loaded: false,
};

export function useHousehold(db: Database | null): [HouseholdView, () => Promise<void>] {
  const [view, setView] = useState<HouseholdView>(EMPTY);

  const reload = useCallback(async () => {
    if (db === null) {
      return;
    }

    const [held, places, listed, list, queued] = await Promise.all([
      items(db),
      locations(db),
      groceries(db),
      shoppingList(db),
      pendingCount(db),
    ]);

    setView({
      items: held,
      locations: places,
      groceries: listed,
      shopping: list,
      queued,
      loaded: true,
    });
  }, [db]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return [view, reload];
}
